import { Suspense, useState, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '@frontegg/react';
import { SideNavbar, TopNavbar } from './groupComponents';
import { useFeatureEntitlements } from '@frontegg/react';

import { getAmbientSoloRoutes } from 'src/modules/ambient_solo/Routes';

import { getSuperAdminRoutes } from 'src/modules/SuperAdmin/Routes';
import { getTemplatesExchangeRoutes } from 'src/modules/TemplatesExchange/Routes';
import { getCelloWidgetRoutes } from 'src/modules/CelloWidget/Routes';
import { debounce } from 'lodash-es';
import { getCareConnectRoutes } from '../modules/appointments/Routes';
import { getSettingsRoutes } from '../modules/appointments/SettingsRoutes';
import { useAppRoutes } from 'src/pages';
import { DemoContainer } from 'src/pages/root';
import { ScreenLoader } from './screenLoader';
import { analytics } from 'src/services';
import { useCello } from 'src/hooks/misc';

export const MasterLayout = () => {
  const { user } = useAuth();
  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);
  const { isEntitled: hasAmbientAccess } = useFeatureEntitlements('ambient');
  const { isEntitled: hasIntakeAccess } = useFeatureEntitlements('intake');
  const { isEntitled: hasFollowupAccess } = useFeatureEntitlements('followup');
  const { isEntitled: isPatientsModuleEntitled } =
    useFeatureEntitlements('patients-module');
  const { isEntitled: isFrontdeskModuleEntitled } =
    useFeatureEntitlements('frontdesk');
  const { isEntitled: isReferralsModuleEntitled } =
    useFeatureEntitlements('referral');
  const { isEntitled: isPhoneTriageModuleEntitled } =
    useFeatureEntitlements('phone-triage');
  const isMobileOrTabletView = window.innerWidth < 1280;
  const [showSidebar, setShowSidebar] = useState(
    isMobileOrTabletView ? false : true
  );
  const [isMinimized, setIsMinimized] = useState(
    'minimizedSideNavBarPreference' in localStorage
      ? localStorage.getItem('minimizedSideNavBarPreference') === 'true'
      : window.innerWidth <= 1440
  );
  const handleToggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const onboarding_status = settingsOrgValue?.onboarding_complete;
  const superAdminAccess =
    (settingsOrgValue?.roles &&
      settingsOrgValue?.roles.includes('superadmin')) ||
    '';

  useEffect(() => {
    analytics.track('Using Layout V1', {
      email: user?.email,
    });
  }, []);

  const { token: celloToken } = useCello();

  const handleResize = useMemo(() => {
    return debounce(() => {
      if (window.innerWidth < 1280) {
        setShowSidebar(false);
      } else if (window.innerWidth >= 1280) {
        setShowSidebar(true);
      }
    }, 100);
  }, []);

  const routes = useAppRoutes();

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // TODO: Fix react useeffect dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getIndexRoute = () => {
    if (isPhoneTriageModuleEntitled) {
      return '/phone-triage';
    }
    if (isReferralsModuleEntitled) {
      return '/referrals';
    }
    if (hasIntakeAccess || hasFollowupAccess) {
      return '/appointments';
    }
    if (hasAmbientAccess) {
      return '/encounters';
    }
    if (isFrontdeskModuleEntitled) {
      return '/frontdesk';
    }
    if (isPatientsModuleEntitled) {
      return '/patients';
    }
    return '/settings';
  };

  return (
    <div
      className={`master-layout ${
        isMinimized ? 'master-layout--nav-minimized' : ''
      }`}
    >
      {onboarding_status ? (
        <SideNavbar
          showSidebar={showSidebar}
          handleToggleSidebar={handleToggleSidebar}
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
        />
      ) : (
        ''
      )}
      {onboarding_status ? (
        <TopNavbar
          handleToggleSidebar={handleToggleSidebar}
          isMinimized={isMinimized}
        />
      ) : null}
      <Suspense fallback={<ScreenLoader />}>
        <Routes>
          {(hasIntakeAccess || hasFollowupAccess) && getCareConnectRoutes()}
          {hasAmbientAccess &&
            getAmbientSoloRoutes({
              isMinimized,
              setIsMinimized,
            })}
          {superAdminAccess &&
            getSuperAdminRoutes({
              isMinimized,
            })}
          {getSettingsRoutes()}
          {getTemplatesExchangeRoutes({
            isMinimized,
          })}
          {celloToken &&
            getCelloWidgetRoutes({
              isMinimized,
            })}
          {routes}
          <Route path="*" element={<Navigate replace to={getIndexRoute()} />} />
        </Routes>
        <DemoContainer />
      </Suspense>
    </div>
  );
};
